import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {useSearchParams} from './useSearchParams';
import { useDispatch, useSelector } from 'react-redux';
import { changeOpcoesResultado } from '../../store/ducks/pesquisa';
import { Store } from '../../store';
import { VERSAO_PESQUISA_PAGINADA } from '../const';

export const usePesquisaPaginadaParams = () => {
    const { search: locationSearch } = useLocation();
    const dispatch = useDispatch();
    const {
        consulta,
        opcoesResultado: {
            paginaAtiva,
            quantPag,
            ordenacaoCombo,
            ordenacao,
            ...restOpcoesResultado
        },
    } = useSelector((state: typeof Store) => state.pesquisa);
    const [filterParams, setFilterParams] = useState<{
        [key: string]: string | null | undefined;
    }>();
    const {
        getAllSearchParams,
        setSearchParam,
        removeSearchParam,
        clearSearchParams,
    } = useSearchParams();

    const params = useMemo(() => {
        const allParams = getAllSearchParams();
        return {
            page: allParams.page ? parseInt(allParams.page, 10) : null,
            perPage: allParams.perPage
                ? parseInt(allParams.perPage, 10)
                : null,
            orderBy: allParams.orderBy || null,
            direction: allParams.direction || null,
        };
    }, [locationSearch]);

    useEffect(() => {
        const allParams = getAllSearchParams();
        const filters = Object.keys(allParams).filter(
            key => key.startsWith('f') && key.length === 2,
        );
        const newFilters = filters.reduce((acc: any, key: string) => {
            return { ...acc, [key]: allParams[key] };
        }, {});
        const hasChanges = !(
            JSON.stringify(filterParams) === JSON.stringify(newFilters)
        );
        if (!hasChanges) return;
        setFilterParams(newFilters);
    }, [locationSearch]);

    useEffect(() => {
        const hasParamsChanges =
            params.page && params.page !== paginaAtiva ||
            params.orderBy && params.orderBy !== quantPag ||
            params.orderBy && params.orderBy !== ordenacaoCombo ||
            params.direction && params.direction !== ordenacao;

        if (
            !consulta ||
            (consulta !== 'error' &&
                consulta.searchVersion === VERSAO_PESQUISA_PAGINADA &&
                hasParamsChanges)
        ) {
            dispatch(
                changeOpcoesResultado({
                    ...restOpcoesResultado,
                    paginaAtiva: params.page || 1,
                    quantPag: params.perPage || 20,
                    ordenacaoCombo: params.orderBy || ordenacaoCombo || false,
                    ordenacao: params.direction || ordenacao || false,
                }),
            );
        }
    }, [params]);

    const setPage = (page: number | null) => {
        if (!page) {
            removeSearchParam('page');
            return;
        }
        setSearchParam('page', `${page}`);
    };

    const setPerPage = (perPage: number | null) => {
        if (!perPage) {
            removeSearchParam('perPage');
            return;
        }
        setSearchParam('perPage', `${perPage}`);
        setSearchParam('page', '1');
    };

    const setOrderBy = (orderBy: string | null) => {
        if (!orderBy) {
            removeSearchParam('orderBy');
            return;
        }
        setSearchParam('orderBy', `${orderBy}`);
        setSearchParam('page', '1');
    };

    const setDirection = (direction: string | null) => {
        if (!direction) {
            removeSearchParam('direction');
            return;
        }
        setSearchParam('direction', `${direction}`);
        setSearchParam('page', '1');
    };

    const addFilter = (key: string, value: string) => {
        if (!filterParams) return;
        const current = filterParams[key];
        if (current) {
            setSearchParam(key, `${current},${value}`);
        } else {
            setSearchParam(key, value);
        }
        setPage(1);
    };

    const removeFilter = (key: string, value: string) => {
        if (!filterParams) return;

        const current = filterParams[key]; // Ex: pode vir algo como "4%252C1"
        if (current) {
            const decoded = decodeURIComponent(decodeURIComponent(current)); //  deixa no formato"4,1"
            const values = decoded.split(',').map(v => v.trim());
            const index = values.indexOf(value);

            if (index !== -1) {
                values.splice(index, 1);
                if (values.length === 0) {
                    removeSearchParam(key);
                } else {
                    // re-encode properly
                    const joined = values.join(',');
                    const encoded = encodeURIComponent(joined); // "4,1" → "4%2C1"
                    setSearchParam(key, encoded);
                }
            }
        }

        setPage(1);
    };

    const clearFilters = () => {
        let allParams = getAllSearchParams();
        const keysToRemove = Object.keys(allParams).filter(
            key => key.startsWith('f') && key.length === 2,
        );
        keysToRemove.forEach(key => delete allParams[key]);
        clearSearchParams(
            new URLSearchParams(
                Object.entries(allParams).map(([key, value]) => [
                    key,
                    value as string,
                ]),
            ),
        );
    };

    return {
        params,
        filterParams,
        setPage,
        setPerPage,
        setOrderBy,
        setDirection,
        addFilter,
        removeFilter,
        clearFilters,
    };
};
